import React, { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { css } from '@emotion/react'
import Modal from '@groovehq/internal-design-system/lib/components/Modal/Modal'
import { rem } from '@groovehq/internal-design-system/lib/util'
import { doSetFlag } from 'ducks/flags/operations'
import {
  selectShouldHideFeaturesAnnoucement,
  selectShouldHideHelplyRebrandAnnouncement,
} from 'ducks/flags/selectors'
import { HIDE_HELPLY_REBRAND_ANNOUNCEMENT } from 'ducks/flags/flagNames'
import { selectIsTestAccount } from 'selectors/account'
import alexAvatar from 'assets/alex-small-alt.png'

const IMAGE_URL =
  'https://cdn.sanity.io/images/wqmhlzkj/production/dedc781973fadc7304c4510da1a9afb3e3ee8040-1675x939.png'

const POST_URL = 'https://helply.com/blog/groove-is-now-helply'

const modalStyles = {
  panel: css`
    padding: 0;
    text-align: left;
  `,
  hero: css`
    width: 100%;
    aspect-ratio: 1675 / 939;
    background-size: cover;
    background-position: center;
    background-color: #1a1a3a;
    border-top-left-radius: ${rem(12)};
    border-top-right-radius: ${rem(12)};
  `,
  content: theme => css`
    padding: ${rem(28)} ${rem(32)} ${rem(32)};
    font-family: ${theme.font.tertiary};
  `,
  title: theme => css`
    font-size: ${rem(24)};
    font-weight: ${theme.fontWeight.bold};
    line-height: 1.2;
    color: ${theme.color.info['900']};
    margin: 0 0 ${rem(16)};
  `,
  byline: css`
    display: flex;
    align-items: center;
    gap: ${rem(12)};
    margin-bottom: ${rem(20)};
  `,
  avatar: css`
    width: ${rem(40)};
    height: ${rem(40)};
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  `,
  bylineName: theme => css`
    font-size: ${rem(14)};
    font-weight: ${theme.fontWeight.semibold};
    color: ${theme.color.info['900']};
    line-height: 1.3;
  `,
  bylineRole: theme => css`
    font-size: ${rem(12)};
    color: rgba(${theme.color.info.black_rgba}, 0.6);
    line-height: 1.3;
  `,
  copy: theme => css`
    font-size: ${rem(15)};
    line-height: 1.55;
    color: rgba(${theme.color.info.black_rgba}, 0.8);
    margin: 0 0 ${rem(24)};
  `,
  button: theme => css`
    display: inline-flex;
    align-items: center;
    padding: ${rem(12)} ${rem(24)};
    border-radius: ${rem(200)};
    background-color: ${theme.color.info['900']};
    color: #fff;
    font-size: ${rem(15)};
    font-weight: ${theme.fontWeight.semibold};
    text-decoration: none;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(${theme.color.info.black_rgba}, 0.85);
      color: #fff;
    }

    &:hover svg {
      transform: translateX(${rem(4)});
    }

    svg {
      margin-left: ${rem(8)};
      transition: transform 0.2s ease;
    }
  `,
}

const HelplyRebrandAnnouncement = () => {
  const dispatch = useDispatch()

  const shouldHideAnnouncement = useSelector(state => {
    const isTestAccount = selectIsTestAccount(state)
    const featuresAnnouncementShowing =
      selectShouldHideFeaturesAnnoucement(state) === false

    return (
      isTestAccount ||
      selectShouldHideHelplyRebrandAnnouncement(state) ||
      featuresAnnouncementShowing
    )
  })

  const [show, setShow] = useState(true)

  const handleClose = useCallback(
    () => {
      dispatch(doSetFlag(HIDE_HELPLY_REBRAND_ANNOUNCEMENT))
      setShow(false)
    },
    [dispatch]
  )

  if (shouldHideAnnouncement !== false) return null

  return (
    <Modal
      portal="#drawer-root"
      open={show}
      onClose={handleClose}
      panelCenter
      shouldCloseOnClickBackdrop
      css={modalStyles.panel}
    >
      <div
        css={modalStyles.hero}
        style={{ backgroundImage: `url(${IMAGE_URL})` }}
        role="img"
        aria-label="Groove is now Helply"
      />
      <div css={modalStyles.content}>
        <h2 css={modalStyles.title}>Groove is now Helply</h2>
        <div css={modalStyles.byline}>
          <img src={alexAvatar} alt="Alex Turnbull" css={modalStyles.avatar} />
          <div>
            <div css={modalStyles.bylineName}>Alex Turnbull</div>
            <div css={modalStyles.bylineRole}>
              Founder, Helply (formerly Groove)
            </div>
          </div>
        </div>
        <p css={modalStyles.copy}>
          What does this Helply thing actually mean for me? The short answer
          nothing. Your account, data, pricing, and workflows are exactly as
          they were — no migration, no price change, and your login still works.
        </p>
        <a
          href={POST_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClose}
          css={modalStyles.button}
        >
          Read the full announcement
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M3.333 8h9.334M8.667 3.333 13.333 8l-4.666 4.667"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </Modal>
  )
}

export default HelplyRebrandAnnouncement
